import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import {
  DashboardOwnership,
  TDashboardGroup,
  TDashboardGroupResponse
} from 'types/GlobalDashboardGroup'
import { TTaskResponse } from 'redux/api/Tasks'
import { formatUsername } from 'utils/functions'

import { sortAlphabeticalAsc, sortDateDesc } from 'utils/sortHelper'
import { toArray } from 'redux/utils'

type Dashboards = ApplicationState['KPIDashboardStore']['dashboards']
type Tasks = ApplicationState['TaskStore']['data']
type StatusById = ApplicationState['ResourceStatusesStore']['byId']

/**
 * Reusable helper to resolve a dashboard group with its dashboards,
 * tasks, and favorite status.
 * - dashboards: resolved from KPIDashboardStore, sorted by index
 * - firstDashboardId: id of the first dashboard, or null if none
 * - tasks: linked via source_type === DASHBOARD and source_id matching
 *   a dashboard that belongs to this group
 * - favorite: true if the group ID is marked favorite in ResourceStatusesStore
 */
function resolveGroup(
  group: TDashboardGroupResponse,
  dashboards: Dashboards,
  tasks: Tasks,
  statusById: StatusById
): TDashboardGroup {
  const groupDashboards = Object.values(dashboards)
    .filter((d) => d.dashboard_group_id === group.id)
    .sort((a, b) => a.index - b.index)

  const dashboardIds = new Set(groupDashboards.map((d) => d.id))
  const status = statusById[group.id]

  return {
    ...group,
    dashboards: groupDashboards,
    favorite: status?.favorite ?? false,
    firstDashboardId: groupDashboards[0]?.id ?? null,
    latest_visit: status?.latest_visit ? new Date(status.latest_visit) : null,
    tasks: Object.values(tasks).filter(
      (task): task is TTaskResponse =>
        task.source_type === 'DASHBOARD' &&
        task.source_id !== null &&
        dashboardIds.has(task.source_id)
    )
  }
}

/**
 * Get dashboard groups that belong to a specific group.
 * NOTE: group.dashboards contains dashboard_group_ids (not dashboard ids).
 *
 * @param state - Application state
 * @param groupId - Group ID
 * @returns Array of dashboard groups belonging to the group
 */
export const selectDashboardGroupsInGroup = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId,
    (_: ApplicationState, groupId: string | null) => groupId
  ],
  (
    dashboardGroups,
    groups,
    dashboards,
    tasks,
    statusById,
    groupId
  ): TDashboardGroup[] => {
    if (!groupId) return []

    const group = groups[groupId]

    if (!group || !group.dashboards) return []

    return group.dashboards
      .map((dashboardGroupId) => dashboardGroups[dashboardGroupId])
      .filter(Boolean)
      .map((group) => resolveGroup(group, dashboards, tasks, statusById))
  }
)

/**
 * Get dashboard group owner (formatted account name).
 *
 * @param state - Application state
 * @param dashboardGroupId - Dashboard group ID
 * @returns Parsed owner with id and formatted name, or null if not found
 */
export const selectDashboardGroupOwner = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.AccountStore.data,
    (_: ApplicationState, dashboardGroupId: string) => dashboardGroupId
  ],
  (
    dashboardGroups,
    users,
    dashboardGroupId
  ): { id: string; name: string } | null => {
    const dashboardGroup = dashboardGroups[dashboardGroupId]

    if (!dashboardGroup) return null

    const owner = users[dashboardGroup.owner]

    if (!owner) return null

    return {
      id: dashboardGroup.owner,
      name: formatUsername(owner)
    }
  }
)

/**
 * Get favorite dashboard groups (sorted alphabetically).
 * Filters dashboard groups where favorite.active is true.
 *
 * @param state - Application state
 * @returns Array of favorite dashboard groups sorted by title
 */
export const selectFavoriteDashboardGroups = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (dashboardGroups, dashboards, tasks, statusById): TDashboardGroup[] => {
    return toArray(dashboardGroups)
      .filter((group) => statusById[group.id]?.favorite)
      .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
      .map((group) => resolveGroup(group, dashboards, tasks, statusById))
  }
)

/**
 * Get recently visited dashboard groups (max 5, sorted by date desc).
 * Filters dashboard groups with latest_visit and returns the 5 most recent.
 *
 * @param state - Application state
 * @returns Array of recently visited dashboard groups (max 5)
 */
export const selectRecentDashboardGroups = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (dashboardGroups, dashboards, tasks, statusById): TDashboardGroup[] => {
    return toArray(dashboardGroups)
      .filter((group) => statusById[group.id]?.latest_visit)
      .sort((a, b) => {
        const visitA = statusById[a.id]?.latest_visit
        const visitB = statusById[b.id]?.latest_visit
        const dateA = visitA ? new Date(visitA) : null
        const dateB = visitB ? new Date(visitB) : null

        return sortDateDesc(dateA, dateB)
      })
      .slice(0, 5)
      .map((group) => resolveGroup(group, dashboards, tasks, statusById))
  }
)

/**
 * Get available dashboard groups (owned by me or shared with me).
 * Filters dashboard groups by ownership and sorts alphabetically.
 *
 * @param state - Application state
 * @returns Array of available dashboard groups sorted by title
 */
export const selectAvailableDashboardGroups = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (dashboardGroups, dashboards, tasks, statusById): TDashboardGroup[] => {
    return toArray(dashboardGroups)
      .filter(
        (group) =>
          group.ownership === DashboardOwnership.SHARED_WITH_ME ||
          group.ownership === DashboardOwnership.OWNED_BY_ME
      )
      .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
      .map((group) => resolveGroup(group, dashboards, tasks, statusById))
  }
)

/**
 * Get dashboard groups in a group with resolved dashboards.
 * Groups without dashboards are filtered out (considered broken).
 *
 * @param state - Application state
 * @param groupId - Group ID
 * @returns Array of dashboard groups belonging to the group with dashboards
 */
export const selectDashboardGroupsInGroupIncludeDashboards = createSelector(
  [selectDashboardGroupsInGroup],
  (dashboardGroups): TDashboardGroup[] => {
    return dashboardGroups.filter((group) => group.firstDashboardId !== null)
  }
)

/**
 * Get available dashboard groups with resolved dashboards.
 * Groups without dashboards are filtered out (considered broken).
 *
 * @param state - Application state
 * @returns Array of available dashboard groups that have dashboards
 */
export const selectAvailableDashboardGroupsIncludeDashboards = createSelector(
  [selectAvailableDashboardGroups],
  (dashboardGroups): TDashboardGroup[] => {
    return dashboardGroups.filter((group) => group.firstDashboardId !== null)
  }
)

/**
 * Get soft-deleted dashboard groups (trash), sorted alphabetically.
 * Deleted groups (and their dashboards) are not present in KPIDashboardStore,
 * so they are resolved from the response's own dashboards array instead of
 * via resolveGroup.
 *
 * @param state - Application state
 * @returns Array of deleted dashboard groups
 */
export const selectDeletedDashboardGroups = createSelector(
  [(state: ApplicationState) => state.DashboardGroupStore.deleted],
  (deleted): TDashboardGroup[] => {
    return toArray(deleted)
      .map((group) => ({
        ...group,
        favorite: false,
        firstDashboardId: group.dashboards[0]?.id ?? null,
        latest_visit: null,
        tasks: []
      }))
      .filter((group) => group.firstDashboardId !== null)
      .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
  }
)

/**
 * Get trash (deleted dashboard groups) loading state.
 *
 * @param state - Application state
 * @returns boolean - true if loading
 */
export const selectDeletedDashboardGroupsLoading = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.deletedFetching,
    (state: ApplicationState) => state.DashboardGroupStore.deletedFetched
  ],
  (fetching, fetched): boolean => {
    return fetching && !fetched
  }
)

/**
 * Get dashboard groups loading state.
 * Returns true if currently fetching and not yet fetched.
 *
 * @param state - Application state
 * @returns boolean - true if loading
 */
export const selectDashboardGroupsLoading = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.fetching,
    (state: ApplicationState) => state.DashboardGroupStore.fetched
  ],
  (fetching, fetched): boolean => {
    return fetching && !fetched
  }
)

/**
 * Get dashboard group from dashboard ID.
 * Fetches the dashboard first, then looks up its dashboard_group_id.
 *
 * @param state - Application state
 * @param dashboardId - Dashboard ID
 * @returns TDashboardGroup | null
 */
export const selectDashboardGroupFromDashboardId = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId,
    (_: ApplicationState, dashboardId: string | null) => dashboardId
  ],
  (
    dashboards,
    dashboardGroups,
    tasks,
    statusById,
    dashboardId
  ): TDashboardGroup | null => {
    if (!dashboardId) return null

    const dashboard = dashboards[dashboardId]

    if (!dashboard) return null

    const group = dashboardGroups[dashboard.dashboard_group_id]

    if (!group) return null

    return resolveGroup(group, dashboards, tasks, statusById)
  }
)

/**
 * Check if dashboard group has many dashboards (more than one).
 *
 * @param state - Application state
 * @param id - Dashboard group ID
 * @returns boolean - true if dashboard group has more than one dashboard
 */
export const selectDashboardGroupHasManyDashboards = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (_: ApplicationState, id: string | null) => id
  ],
  (dashboards, id): boolean => {
    if (!id) return false

    return (
      Object.values(dashboards).filter(
        (dashboard) => dashboard.dashboard_group_id === id
      ).length > 1
    )
  }
)

/**
 * Get a specific dashboard group by ID with resolved dashboards and tasks.
 * Returns null if the group has no dashboards (considered broken).
 *
 * @param state - Application state
 * @param id - Dashboard group ID
 * @returns TDashboardGroup | null
 */
export const selectDashboardGroup = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (state: ApplicationState) => state.TaskStore.data,
    (state: ApplicationState) => state.ResourceStatusesStore.byId,
    (_: ApplicationState, id: string | null) => id
  ],
  (
    dashboardGroups,
    dashboards,
    tasks,
    statusById,
    id
  ): TDashboardGroup | null => {
    if (!id) return null

    const group = dashboardGroups[id]

    if (!group) return null

    const resolved = resolveGroup(group, dashboards, tasks, statusById)

    if (!resolved.firstDashboardId) return null

    return resolved
  }
)
